import Vue from "vue";
import {TinyEmitter} from "tiny-emitter";
import {analyticsController} from "src/modules/user/analytics/AnalyticsController";

// All false negative, (a lot of false negatives linked to WebMidi and TS
// @ts-ignore
// eslint-disable-next-line no-undef
type MIDIConnectionEvent = WebMidi.MIDIConnectionEvent;
// @ts-ignore
// eslint-disable-next-line no-undef
type MIDIMessageEvent = WebMidi.MIDIMessageEvent;
// @ts-ignore
// eslint-disable-next-line no-undef
type MIDIAccess = WebMidi.MIDIAccess;

import {isAnOffMessage, isAnOnMessage, MIDI_PITCH_TO_KEY_OFFSET, MidiMessageIndex} from './MidiUtils';
import {synthController} from "../synth/SynthController";
import {MidiKeyEvent} from './models/MidiKeyEvent';
import {isInIOSApp, isInMobileApp} from "src/utils/MobileUtils";
import { MidiConfigDevice } from "./models/MidiConfigDevice";
import * as Audio from "../audio"
interface MidiDispatcherState {
  isActive: boolean
  device: MidiConfigDevice | null
  activeKeys: number[]
}

class MidiDispatcher extends TinyEmitter {

  public readonly state = Vue.observable<MidiDispatcherState>({
    isActive: false,
    device: null,
    activeKeys: []
  });

  // Mirrors state.activeKeys for O(1) lookups — avoids O(n) Array.includes on every MIDI message
  private readonly activeKeysSet = new Set<number>();

  /* Setup */

  initMidiDispatcher() {
    if (isInMobileApp() && isInIOSApp()) {
      this.initIOSMidi();
    }
    else {
      this.initWebMidiIfAvailable();
    }
  }

  /* Ios Midi Api */

  private iosMidiDeviceChangedHandler = (event: any) => this.onMidiStateChange(event);
  private iosMidiNoteReceivedHandler = (event: any) => this.onMidiMessageReceived(event.data);

  private initIOSMidi() {
    // @ts-ignore - Midi is provided by Capacitor plugin
    // eslint-disable-next-line no-undef
    Midi.initListeners();
    document.addEventListener("iOSMidiDeviceChanged", this.iosMidiDeviceChangedHandler, false);
    document.addEventListener("iOSMidiNoteReceived", this.iosMidiNoteReceivedHandler, false);
  }

  /* Web Midi Api */
  private midiController: MIDIAccess|null = null;

  private initWebMidiIfAvailable() {
    if (navigator.requestMIDIAccess) {
      navigator.requestMIDIAccess({sysex: false}).then(
      // @ts-ignore
        (midiData: MIDIAccess) =>
          this.onMidiSuccess(this, midiData), MidiDispatcher.onMidiFailure
      )
    }
  }

  private listenToAllMidiControllerInputs() {
    const allInputs = this.midiController!.inputs.values();

    for (let input = allInputs.next(); input && !input.done; input = allInputs.next()) {
      input.value.onmidimessage = (message: MIDIMessageEvent) => this.onMidiMessageReceived(message.data);
    }
  }

  private onMidiSuccess(midiListenerStore: MidiDispatcher, midiData: MIDIAccess) {
    midiListenerStore.midiController = midiData;
    midiListenerStore.midiController.onstatechange = (e: MIDIConnectionEvent) => {
      this.onMidiStateChange(e.port);
      if (e.port.state === "connected") {
        this.listenToAllMidiControllerInputs();
      }
    };

    // Handle already-connected keyboards
    midiListenerStore.listenToAllMidiControllerInputs();
    const inputs = midiData.inputs.values();
    for (let input = inputs.next(); input && !input.done; input = inputs.next()) {
      if (input.value.state === 'connected') {
        this.onMidiStateChange(input.value);
      }
    }
  }

  private static onMidiFailure() {
    console.log('Could not access your MIDI devices.');
  }

  /* Midi Listener */
  private onMidiStateChange(midiEvent: any) {
    const deviceState = isInIOSApp() ? (midiEvent[0] === 1 ? 'connected' : 'disconnected') : midiEvent.state;

    this.state.device = {
      name: midiEvent.name,
      type: midiEvent.type,
      manufacturer: midiEvent.manufacturer,
      state: deviceState
    };
    this.state.isActive = deviceState === 'connected';

    if(this.state.isActive) {
      Audio.musicTranscriptionController.terminate()
    }
    else {
      // A disconnected device can never send the note-off for a key that was held when it
      // dropped — without clearing here, that key stays "active" forever, and any HOLD note at
      // that key would keep validating as correctly held indefinitely.
      this.clearActiveKeys();
    }

    analyticsController.logAnalyticsEvent({
      title: (this.state.isActive) ? 'connected midi device' : 'unconnected midi device',
      category: 'Midi Events',
      gaLabelIdentifier: 'name',
      parameters: midiEvent
    });
  }

  public clearActiveKeys() {
    this.activeKeysSet.clear();
    this.state.activeKeys.length = 0;
  }

  private onMidiMessageReceived(messageData: Uint8Array) {
    const key = messageData[MidiMessageIndex.PITCH] + MIDI_PITCH_TO_KEY_OFFSET;
    const midiKeyEvent = new MidiKeyEvent(key, messageData[MidiMessageIndex.VELOCITY]);
    this.state.isActive = true;
    if (this.state.device && this.state.device.state !== 'connected') {
      this.state.device.state = 'connected';
    }

    if (isAnOnMessage(messageData)) {
      synthController.triggerAttack(midiKeyEvent);
      this.addToActiveNote(midiKeyEvent);
      this.emit('notePlayed', midiKeyEvent);
    }

    if (isAnOffMessage(messageData)) {
      synthController.triggerRelease(midiKeyEvent);
      this.removeFromActiveNote(midiKeyEvent);
      this.emit('notesStopped', midiKeyEvent);
    }

    this.emit('messageEvent', messageData);
  }

  /* Active Keys */

  private addToActiveNote(noteMessage: MidiKeyEvent) {
    if (!this.activeKeysSet.has(noteMessage.key)) {
      this.activeKeysSet.add(noteMessage.key);
      this.state.activeKeys.push(noteMessage.key);
      this.emit('activeKeysChanged', this.state.activeKeys);
    }
  }

  private removeFromActiveNote(noteMessage: MidiKeyEvent) {
    if (this.activeKeysSet.has(noteMessage.key)) {
      this.activeKeysSet.delete(noteMessage.key);
      const activeKeys = this.state.activeKeys;
      const idx = activeKeys.indexOf(noteMessage.key);
      if (idx !== -1) {
        // Swap with the last element and pop instead of splice: no consumer of activeKeys
        // relies on ordering (indexOf/includes/forEach/sort), so this avoids shifting every
        // remaining element down by one.
        activeKeys[idx] = activeKeys[activeKeys.length - 1];
        activeKeys.pop();
      }
      this.emit('activeKeysChanged', this.state.activeKeys);
    }
  }

  /* Cleanup */
  public cleanup() {
    if (isInMobileApp() && isInIOSApp()) {
      document.removeEventListener("iOSMidiDeviceChanged", this.iosMidiDeviceChangedHandler, false);
      document.removeEventListener("iOSMidiNoteReceived", this.iosMidiNoteReceivedHandler, false);
    }

    if (this.midiController) {
      const allInputs = this.midiController.inputs.values();
      for (let input = allInputs.next(); input && !input.done; input = allInputs.next()) {
        input.value.onmidimessage = null;
      }
      this.midiController.onstatechange = null;
    }

    this.clearActiveKeys();
    this.off('notePlayed');
    this.off('notesStopped');
    this.off('messageEvent');
    this.off('activeKeysChanged');
  }
}

export const midiDispatcher = new MidiDispatcher();
